import React, { useState, useEffect } from 'react';
import { employeeAPI, documentAPI, googleSheetsAPI } from '../../services/api';
import { useGroup } from '../../contexts/GroupContext';
import { formatDate, formatTimestamp, getStatusBadgeClass } from '../../utils/helpers';
import { SUCCESS_MESSAGES, ERROR_MESSAGES } from '../../utils/constants';
import EmployeeForm from './EmployeeForm';
import LoadingSpinner from '../Common/LoadingSpinner';
import ErrorMessage from '../Common/ErrorMessage';
import SuccessMessage from '../Common/SuccessMessage';

const EmployeeList = () => {
  const { selectedGroupId, groups } = useGroup();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [filterGroupId, setFilterGroupId] = useState(selectedGroupId || ''); // Group filter for table
  const [filterConsent, setFilterConsent] = useState(''); // Consent filter: '', 'true', or 'false'
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(false); // Checkbox for updating existing employees

  // Fetch employees
  const fetchEmployees = async (searchQuery = null) => {
    try {
      setLoading(true);
      setError(null);
      
      // Use filterGroupId if set, otherwise use selectedGroupId
      const groupIdToFilter = filterGroupId || selectedGroupId;
      
      let response;
      // Convert filterConsent to boolean or null
      const consentFilter = filterConsent === '' ? null : (filterConsent === 'true');
      
      if (searchQuery && searchQuery.trim()) {
        // Use search API if there's a search query
        // Note: Search API might not support consent filter, so we filter client-side if needed
        response = await employeeAPI.search(searchQuery.trim(), page, pageSize);
        // If consent filter is set, filter results client-side
        if (consentFilter !== null && response.data.data) {
          response.data.data = response.data.data.filter(emp => emp.consent === consentFilter);
          response.data.count = response.data.data.length;
          response.data.pagination = {
            ...response.data.pagination,
            total: response.data.data.length
          };
        }
      } else {
        // Use regular getAll API if no search query
        response = await employeeAPI.getAll(page, pageSize, groupIdToFilter, consentFilter);
      }
      
      setEmployees(response.data.data || []);
      setTotal(response.data.pagination?.total || response.data.count || 0);
    } catch (err) {
      setError(ERROR_MESSAGES.NETWORK_ERROR);
      console.error('Error fetching employees:', err);
    } finally {
      setLoading(false);
    }
  };

  // Delete employee
  const handleDelete = async (employeeId) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) {
      return;
    }

    try {
      await employeeAPI.delete(employeeId);
      setSuccess(SUCCESS_MESSAGES.EMPLOYEE_DELETED);
      fetchEmployees(filterText && filterText.trim() ? filterText : null);
    } catch (err) {
      setError(ERROR_MESSAGES.NETWORK_ERROR);
      console.error('Error deleting employee:', err);
    }
  };

  // Update consent
  const handleConsentUpdate = async (employeeId, currentConsent) => {
    try {
      await employeeAPI.updateConsent(employeeId, !currentConsent);
      setSuccess(SUCCESS_MESSAGES.CONSENT_UPDATED);
      fetchEmployees(filterText && filterText.trim() ? filterText : null);
    } catch (err) {
      setError(ERROR_MESSAGES.NETWORK_ERROR);
      console.error('Error updating consent:', err);
    }
  };

  // Handle form submission
  const handleFormSubmit = () => {
    setShowForm(false);
    setEditingEmployee(null);
    setSuccess(SUCCESS_MESSAGES.EMPLOYEE_CREATED);
    fetchEmployees(filterText && filterText.trim() ? filterText : null);
  };

  // Handle edit
  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    console.log('clicked on the edit :: ', employee);
    
    setShowForm(true);
  };

  // Handle create new
  const handleCreateNew = () => {
    setEditingEmployee(null);
    setShowForm(true);
  };

  // Generate documents for all employees
  const handleGenerateAll = async () => {
    if (!window.confirm('Generate documents (PDF/XML) with IDCER for all employees? This will regenerate existing documents.')) return;
    try {
      setGeneratingAll(true);
      setError(null);
      const payload = {
        doc_type: 'IDCER',
        issuer_id: 'in.gov.maharashtrasrpf',
        issue_date: new Date().toISOString().slice(0, 10),
        regenerate: true, // Regenerate even if documents exist
      };
      const res = await documentAPI.generateAll(payload);
      const created = res?.data?.data?.created ?? 0;
      const updated = res?.data?.data?.updated ?? 0;
      const skipped = res?.data?.data?.skipped ?? 0;
      const errors = res?.data?.data?.errors ?? 0;
      const message = `Generated ${created} new, updated ${updated}, skipped ${skipped}.${errors > 0 ? ` Errors: ${errors}` : ''}`;
      setSuccess(message);
    } catch (err) {
      console.error('Generate all documents error:', err);
      setError(err?.response?.data?.error || ERROR_MESSAGES.NETWORK_ERROR);
    } finally {
      setGeneratingAll(false);
    }
  };

  // Sync employees from Google Sheets
  const handleSyncFromGoogleSheets = async () => {
    const confirmMessage = updateExisting 
      ? 'Sync employees from Google Sheets? This will create new employees AND update existing ones based on employee ID and group.'
      : 'Sync employees from Google Sheets? This will only create new employees (existing ones will be skipped).';
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      setSyncing(true);
      setError(null);
      setSuccess(null);
      
      const response = await googleSheetsAPI.syncEmployees(updateExisting);
      const data = response.data.data;
      
      const message = response.data.message || 
        `Sync completed: ${data.created || 0} created, ${data.updated || 0} updated, ${data.skipped || 0} skipped${data.errors?.length > 0 ? `, ${data.errors.length} errors` : ''}`;
      
      setSuccess(message);
      
      // Refresh employee list
      await fetchEmployees(null);
      
      // Show errors if any
      if (data.errors && data.errors.length > 0) {
        console.warn('Sync errors:', data.errors);
        setTimeout(() => {
          setError(`Some rows had errors. Check console for details. First error: ${data.errors[0]}`);
        }, 2000);
      }
    } catch (err) {
      console.error('Sync from Google Sheets error:', err);
      setError(err?.response?.data?.error || 'Failed to sync from Google Sheets. Make sure configuration is set in Settings.');
    } finally {
      setSyncing(false);
    }
  };

  // Handle search input change with debouncing
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setFilterText(value);
    setPage(1); // Reset to first page when searching
    
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // If search is cleared, fetch immediately
    if (!value.trim()) {
      fetchEmployees(null);
      return;
    }
    
    // Debounce search API call (wait 500ms after user stops typing)
    const timeout = setTimeout(() => {
      fetchEmployees(value);
    }, 500);
    
    setSearchTimeout(timeout);
  };

  // Update filterGroupId when selectedGroupId changes (sync with header group selector)
  useEffect(() => {
    // Always sync filterGroupId with selectedGroupId when it changes
    // This ensures the header group selector updates the employee list immediately
    setFilterGroupId(selectedGroupId || '');
    setPage(1); // Reset to first page when group changes
  }, [selectedGroupId]);

  // Fetch employees when page or pageSize changes (but not when filterText changes - handled by debounce)
  useEffect(() => {
    fetchEmployees(filterText && filterText.trim() ? filterText : null);
    // Cleanup timeout on unmount
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, filterGroupId, filterConsent]);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  if (loading && employees.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="employee-list">
      <div className="page-header">
        <h2>Employee Management</h2>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn btn-outline-primary  sync-btn" onClick={handleSyncFromGoogleSheets} disabled={syncing}>
              {syncing ? 'Syncing…' : (
                <>
                  <i class="fa-solid fa-rotate"></i> Sync from Google Sheets
                </>
              )}
            </button>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.875rem', color: '#475569' }}>
              <input
                type="checkbox"
                checked={updateExisting}
                onChange={(e) => setUpdateExisting(e.target.checked)}
                disabled={syncing}
                style={{ cursor: 'pointer' }}
              />
              <span>Update existing employees</span>
            </label>
          </div>
          <button className="btn btn-outline-primary " onClick={handleGenerateAll} disabled={generatingAll}>
            {generatingAll ? 'Generating…' : 'Generate All Documents'}
          </button>
          <button className="btn btn-primary" onClick={handleCreateNew}>
           <i class="fa-solid fa-plus"></i>  Add New Employee
          </button>
        </div>
      </div>

      {error && <ErrorMessage message={error} onClose={() => setError(null)} />}
      {success && <SuccessMessage message={success} onClose={() => setSuccess(null)} />}

      <div className="search-bar" style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <span style={{ 
            position: 'absolute', 
            left: '12px', 
            top: '50%', 
            transform: 'translateY(-50%)',
            color: '#0F2854',
            fontSize: '1rem'
          }}><i className="fa-solid fa-magnifying-glass"></i></span>
          <input
            type="text"
            placeholder="Search employees by name, ID, department, mobile, or designation..."
            value={filterText}
            onChange={handleSearchChange}
            className="form-control"
            style={{ 
              width: '100%',
              paddingLeft: '40px',
              borderRadius: '0.5rem',
              border: '1px solid #e2e8f0',
              fontSize: '0.875rem',
              transition: 'all 0.2s ease'
            }}
            onFocus={(e) => e.target.style.borderColor = '#0F2854'}
            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
          />
          {filterText && (
            <span style={{ 
              position: 'absolute',
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#64748b', 
              fontSize: '0.875rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.25rem'
            }}>
              {total} result{total !== 1 ? 's' : ''} found
              {loading && <span style={{ marginLeft: '8px', color: '#3b82f6' }}>⏳</span>}
            </span>
          )}
        </div>
        <div style={{ minWidth: '180px' }}>
          <select
            className="form-control"
             style={{ 
              borderRadius: '0.5rem',
              border: '1px solid #e2e8f0',
              fontSize: '0.875rem',
              transition: 'all 0.2s ease'
            }}
            value={filterGroupId || ''}
            onChange={(e) => {
              setFilterGroupId(e.target.value ? parseInt(e.target.value) : '');
              setPage(1); // Reset to first page when filter changes
            }}
          >
            <option value="">All Groups</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>
        </div>
        <div style={{ minWidth: '150px' }}>
          <select
            className="form-control"
             style={{ 
              borderRadius: '0.5rem',
              border: '1px solid #e2e8f0',
              fontSize: '0.875rem',
              transition: 'all 0.2s ease'
            }}
            value={filterConsent}
            onChange={(e) => {
              setFilterConsent(e.target.value);
              setPage(1); // Reset to first page when filter changes
            }}
          >
            <option value="">All Consent</option>
            <option value="true">Consent: Yes</option>
            <option value="false">Consent: No</option>
          </select>
        </div>
      </div>

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Employee ID</th>
              <th>Full Name</th>
              <th>Department</th>
              <th>Designation</th>
              <th>Group</th>
              <th>DOB</th>
              <th>Consent</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {employees.map((employee) => {
              // Get group name - prefer from API response, fallback to groups context
              let groupName = 'N/A';
              if (employee.group?.name) {
                groupName = employee.group.name;
              } else if (employee.group_id) {
                const employeeGroup = groups.find(g => g.id === employee.group_id);
                groupName = employeeGroup ? employeeGroup.name : `Group ${employee.group_id}`;
              }
              
              return (
                <tr key={employee.id}>
                  <td>{employee.employee_id}</td>
                  <td>{employee.full_name}</td>
                  <td>{employee.department || '-'}</td>
                  <td>{employee.designation || '-'}</td>
                  <td>
                    <span className="badge badge-info" style={{ padding: '4px 8px', borderRadius: '4px' }}>
                      {groupName}
                    </span>
                  </td>
                  <td>{formatDate(employee.dob)}</td>
                  <td>
                    <span className={`badge ${employee.consent ? 'badge-success' : 'badge-danger'}`}>
                      {employee.consent ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>{formatTimestamp(employee.created_at)}</td>
                  <td>
                  <div className="action-buttons">
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => handleEdit(employee)}
                      title="Edit"
                    >
                      <i class="fa-solid fa-pen-to-square"></i>
                    </button>
                    <button
                      className="btn btn-sm btn-outline-warning"
                      onClick={() => handleConsentUpdate(employee.employee_id, employee.consent)}
                      title="Toggle Consent"
                    >
                      {employee.consent ? <i class="fa-solid fa-xmark"></i> : <i class="fa-regular fa-square-check"></i> }
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(employee.employee_id)}
                      title="Delete"
                    >
                      <i class="fa-solid fa-trash-can"></i>
                    </button>
                  </div>
                </td>
              </tr>
            );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
        <div>
          <button className="btn btn-outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</button>
          <span style={{ margin: '0 8px' }}>Page {page}</span>
          <button className="btn btn-outline" onClick={() => setPage(p => (p * pageSize < total ? p + 1 : p))} disabled={page * pageSize >= total}>Next</button>
        </div>
        <div>
          <label style={{ marginRight: 8 }}>Rows per page</label>
          <select value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }}>
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
          <span style={{ marginLeft: 12 }}>{Math.min((page - 1) * pageSize + 1, total)}–{Math.min(page * pageSize, total)} of {total}</span>
        </div>
      </div>

      {employees.length === 0 && !loading && (
        <div className="no-data">
          <p>{filterText ? `No employees found matching "${filterText}"` : 'No employees found.'}</p>
        </div>
      )}

      {showForm && (
        <EmployeeForm
          employee={editingEmployee}
          onClose={() => {
            setShowForm(false);
            setEditingEmployee(null);
          }}
          onSubmit={handleFormSubmit}
        />
      )}
    </div>
  );
};

export default EmployeeList;
